import { IconArrowLeft, IconPlus, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  AdminPageHeader,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  IconAction,
  Select,
  Stack,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useConfirmDialog,
} from "@/components/ui";
import { listarTodasLasAreas } from "@/features/admin-areas/services/areas-service";
import { listarUsuariosAdmin } from "@/features/admin-usuarios/services/usuarios-service";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { gestionDocumentalApi, type AmbitoRegla, type ReglaDeRevision } from "../gestion-documental-service";

const QK = ["admin", "reglas-revision-documental"] as const;

/**
 * Gestión Documental (fase 2) · reglas que hacen obligatoria (o no) la revisión documental para un
 * ÁREA de la solicitud o para un SOLICITANTE (D-10/D-12). Gana la más específica: solicitante &gt;
 * área &gt; tipo de documento &gt; categoría (D-11) · por defecto la revisión no es obligatoria.
 */
export function AdminReglasRevisionPage() {
  const navigate = useNavigate();
  const { data: reglas = [], isLoading } = useQuery({ queryKey: QK, queryFn: gestionDocumentalApi.reglas });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <AdminPageHeader
        title="Reglas de revisión documental"
        description="Hacen obligatoria (o no) la revisión de los documentos para un área o un solicitante · gana la regla más específica."
        actions={
          <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => void navigate({ to: "/admin" })}>
            Volver
          </Button>
        }
      />
      <Stack gap="md">
        <Alert variant="info" title="Precedencia">
          Solicitante &gt; área de la solicitud &gt; tipo de documento &gt; categoría. Sin ninguna regla, la revisión no es obligatoria.
          La revisión documental se activa en la siguiente fase del módulo; aquí ya puedes dejarla configurada.
        </Alert>
        <SeccionReglas ambito="Area" titulo="Por área" reglas={reglas.filter((r) => r.ambito === "Area")} cargando={isLoading} />
        <SeccionReglas ambito="Usuario" titulo="Por solicitante" reglas={reglas.filter((r) => r.ambito === "Usuario")} cargando={isLoading} />
      </Stack>
    </div>
  );
}

function SeccionReglas({ ambito, titulo, reglas, cargando }: { ambito: AmbitoRegla; titulo: string; reglas: ReglaDeRevision[]; cargando: boolean }) {
  const qc = useQueryClient();
  const confirm = useConfirmDialog();
  const invalidar = () => void qc.invalidateQueries({ queryKey: QK });

  const guardar = useMutation({
    mutationFn: ({ referenciaId, requiereRevision }: { referenciaId: string; requiereRevision: boolean }) =>
      gestionDocumentalApi.guardarRegla(ambito, referenciaId, requiereRevision),
    onSuccess: (r) => {
      toastExito({ title: "Regla guardada", message: `${r.nombreReferencia} · ${r.requiereRevision ? "revisión obligatoria" : "sin revisión obligatoria"}` });
      invalidar();
    },
    onError: toastErrorApi,
  });
  const eliminar = useMutation({
    mutationFn: gestionDocumentalApi.eliminarRegla,
    onSuccess: () => {
      toastExito({ title: "Regla eliminada", message: "Aplica de nuevo el siguiente nivel de la precedencia." });
      invalidar();
    },
    onError: toastErrorApi,
  });

  const confirmarEliminar = async (r: ReglaDeRevision) => {
    const ok = await confirm({
      title: "Eliminar regla",
      message: <>¿Eliminar la regla de <b>{r.nombreReferencia}</b>? Aplicará el siguiente nivel de la precedencia.</>,
      confirmLabel: "Eliminar",
      confirmColor: "peligro",
    });
    if (ok) eliminar.mutate(r.id);
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Text fw={600}>{titulo}</Text>
        <NuevaRegla ambito={ambito} excluir={reglas.map((r) => r.referenciaId)} guardando={guardar.isPending} onGuardar={(referenciaId, requiere) => guardar.mutate({ referenciaId, requiereRevision: requiere })} />
        <Table verticalSpacing="sm">
          <Thead>
            <Tr>
              <Th>{ambito === "Area" ? "Área" : "Solicitante"}</Th>
              <Th className="w-56">Revisión obligatoria</Th>
              <Th className="w-16" />
            </Tr>
          </Thead>
          <Tbody>
            {reglas.map((r) => (
              <Tr key={r.id}>
                <Td>
                  <Text size="sm">{r.nombreReferencia}</Text>
                </Td>
                <Td>
                  <Group gap="xs">
                    <Switch checked={r.requiereRevision} disabled={guardar.isPending} onCheckedChange={(c) => guardar.mutate({ referenciaId: r.referenciaId, requiereRevision: c })} />
                    <Badge size="xs" color={r.requiereRevision ? "advertencia" : "slate"}>
                      {r.requiereRevision ? "Obligatoria" : "No obligatoria"}
                    </Badge>
                  </Group>
                </Td>
                <Td align="right">
                  <IconAction label="Eliminar regla" variant="subtle" iconColor="peligro-600" onClick={() => void confirmarEliminar(r)}>
                    <IconTrash size={16} stroke={1.7} />
                  </IconAction>
                </Td>
              </Tr>
            ))}
            {!cargando && reglas.length === 0 && (
              <Tr>
                <Td colSpan={3}>
                  <Text size="sm" c="dimmed" ta="center" className="py-3">
                    Sin reglas {ambito === "Area" ? "por área" : "por solicitante"}.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Stack>
    </Card>
  );
}

function NuevaRegla({
  ambito,
  excluir,
  guardando,
  onGuardar,
}: {
  ambito: AmbitoRegla;
  excluir: string[];
  guardando: boolean;
  onGuardar: (referenciaId: string, requiereRevision: boolean) => void;
}) {
  const [referencia, setReferencia] = useState<string>("");
  const [requiere, setRequiere] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaDebounced] = useDebouncedValue(busqueda, 350);

  const { data: areas = [] } = useQuery({ queryKey: ["admin", "areas", "todas", "reglas"], queryFn: listarTodasLasAreas, enabled: ambito === "Area" });
  const { data: usuarios } = useQuery({
    queryKey: ["admin", "usuarios", "reglas", busquedaDebounced],
    queryFn: () => listarUsuariosAdmin({ busqueda: busquedaDebounced || undefined, pageSize: 50 }),
    enabled: ambito === "Usuario",
  });

  const opciones =
    ambito === "Area"
      ? areas.filter((a) => !excluir.includes(a.id)).map((a) => ({ value: a.id, label: a.nombre }))
      : (usuarios?.items ?? []).filter((u) => !excluir.includes(u.id)).map((u) => ({ value: u.id, label: u.fullName ? `${u.fullName} · ${u.email}` : u.email }));

  return (
    <Group gap="md" align="flex-end">
      {ambito === "Usuario" && (
        <div className="w-64">
          <label className="mb-1 block text-sm font-medium">Buscar solicitante</label>
          <input
            value={busqueda}
            onChange={(e) => setBusqueda(e.currentTarget.value)}
            placeholder="Nombre o correo…"
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-multiva-600"
          />
        </div>
      )}
      <Select
        label={ambito === "Area" ? "Área" : "Solicitante"}
        options={opciones}
        value={referencia || undefined}
        onValueChange={setReferencia}
        placeholder={ambito === "Area" ? "Selecciona el área…" : "Selecciona el usuario…"}
        searchable
        className="w-80"
      />
      <Switch label="Revisión obligatoria" checked={requiere} onCheckedChange={setRequiere} />
      <Button
        leftSection={<IconPlus size={14} />}
        disabled={!referencia}
        loading={guardando}
        onClick={() => {
          onGuardar(referencia, requiere);
          setReferencia("");
        }}
      >
        Agregar regla
      </Button>
    </Group>
  );
}
